import React from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { store } from '@/store/store';

import { Unsubscribe } from '@/pages/unsubscribe/Unsubscribe';

import { createAppTheme } from '@/theme';
import { RoutePath } from '@/utils/enums';

import App from './App';
import { Auth0ProviderWithRedirectCallback } from './components/auth/Auth0ProviderWithRedirectCallback';
import { AuthGuard } from './components/auth/AuthGuard';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { SnackbarProvider } from './context/snackbar/SnackbarContext';

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

// Validate Auth0 configuration
if (!domain || !clientId) {
  console.error('Auth0 configuration missing. Please check your .env file.');
  console.error('Required environment variables:');
  console.error('- VITE_AUTH0_DOMAIN');
  console.error('- VITE_AUTH0_CLIENT_ID');
  throw new Error('Auth0 domain and client ID must be set in .env file');
}

// Validate domain format
if (
  !domain.includes('.auth0.com') &&
  !domain.includes('.us.auth0.com') &&
  !domain.includes('.eu.auth0.com') &&
  !domain.includes('.au.auth0.com')
) {
  console.warn(
    'Auth0 domain format might be incorrect. Expected format: your-domain.auth0.com'
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = ReactDOM.createRoot(rootElement);

// The Unsubscribe link in alert email opens this page signed out (RME-1362).
// It renders without Auth0, the Redux store or the AuthGuard, so a recipient
// is never sent to sign-in, and the page makes no authenticated call.
// A trailing slash is allowed, so a proxy that appends one cannot send the
// recipient to sign-in.
if (globalThis.location.pathname.replace(/\/$/, '') === RoutePath.UNSUBSCRIBE) {
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={createAppTheme(false)}>
        <CssBaseline />
        <Unsubscribe />
      </ThemeProvider>
    </React.StrictMode>
  );
} else {
  renderApp();
}

function renderApp() {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <Auth0ProviderWithRedirectCallback
          domain={domain}
          clientId={clientId}
          authorizationParams={{
            audience: audience,
          }}
        >
          <SnackbarProvider>
            <Provider store={store}>
              <AuthGuard>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </AuthGuard>
            </Provider>
          </SnackbarProvider>
        </Auth0ProviderWithRedirectCallback>
      </BrowserRouter>
    </React.StrictMode>
  );
}
